import { useLazyQuery, useMutation } from "@apollo/client/react"; import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";

import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { ID_TIPO_VISITA_PRESENCIAL, ID_TIPO_VISITA_TELEENFERMERIA } from "../../../MisVisitasCreationContent";
import SAVE_FINALIZAR_VISITA, { adaptAddVisitaFinalizarInput } from "../../../../../../data/graphQL/mutations/Visitas/saveFinalizarVisita";
import { CustomButton } from "../../../../../../components/customButton/CustomButton";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import GET_COMBO_VISITA_VALOR, { adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import VisitaFirmaInput from "../../VisitaFirmaInput/VisitaFirmaInput";
import moment from "moment";
import { obtenerCoordenadas } from "../../../../constants/getUbicacion";
import { useValidacionDistanciaVisita } from "../../../../hooks/useValidacionDistanciaVisita";
import { useValidacionDiscrepanciaHorariaVisita } from "../../../../hooks/useValidacionDiscrepanciaHorariaVisita";

const COMBO_VISITA_INERLOCUTOR = 'Interlocutor';

export default function FinalizacionVisita({
  readOnly,
  onSaveRef,
  isClone,
  onExposeFormatted,
  combosVisita,
  figuraProfesional,
  esVisitaCero
}) {
  const navigate = useNavigate();
  const { validarDistancia, modal: modalDistancia } = useValidacionDistanciaVisita();
  const { validarDiscrepancia, modal: modalDiscrepancia } = useValidacionDiscrepanciaHorariaVisita();

  const esConsultor = ["CONSULTOR"].includes(figuraProfesional?.trim()?.toUpperCase());
  const idUsuario = parseInt(sessionStorage.getItem("idUsuario"), 10);

  const [formData, setFormData] = useState({});
  const [finalizarLoading, setFinalizarLoading] = useState(false);
  const [comboInterlocutorOptions, setComboInterlocutorOptions] = useState([]);

  const { visita, setVisita } = useVisita();

  const [loadComboInterlocutor, { data: dataInterlocutor }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (visita?.idUsuarioCreador !== idUsuario && !readOnly) {
      handleChange('conformidad', false);
    }
  }, [idUsuario, visita?.idUsuarioCreador]);

  useEffect(() => {
    if (!dataInterlocutor) return;
    setComboInterlocutorOptions(adaptCombosVisitas(dataInterlocutor));
  }, [dataInterlocutor]);

  const [save] = useMutation(SAVE_FINALIZAR_VISITA);

  const {
    ROL_PACIENTE,
    ROL_FAMILIAR,
    ROL_CUIDADOR
  } = useMemo(() => {
    const getId = (name) => comboInterlocutorOptions.find((opt) => opt.displayName === name)?.id;
    return {
      ROL_PACIENTE: getId("Paciente"),
      ROL_FAMILIAR: getId("Familiar"),
      ROL_CUIDADOR: getId("Cuidador")
    };
  }, [comboInterlocutorOptions]);

  const formattedGroups = useMemo(() => {
    const esPresencial = visita?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_PRESENCIAL;
    const esVirtual = !esPresencial;

    return groups.map((group) => {
      const newGroup = { ...group };
      // Ocultar grupos/inputs según modalidad
      if (group.key === 'presencial' && !esPresencial) newGroup.hidden = true;
      if (group.key === 'virtual' && !esVirtual) newGroup.hidden = true;

      const formattedInputs = newGroup.inputs.map((input) => {
        const newInput = { ...input };

        switch (input.name) {
          // PRESENCIAL
          case 'conformidad':
            newInput.disabled = !!visita?.idUsuarioCreador && visita?.idUsuarioCreador != idUsuario;
            break;
          case 'obsConformidad':
            newInput.hidden = !(esPresencial && formData?.conformidad === false);
            newInput.required = esPresencial && formData?.conformidad === false;
            break;
          case 'idComboVisitaValorInterlocutor':
            newInput.options = comboInterlocutorOptions;
            break;
          case 'firmaNombrePacienteInfo':
            newInput.hidden = !(esPresencial && formData?.idComboVisitaValorInterlocutor === ROL_PACIENTE);
            newInput.fixedValue = visita?.paciente || "-";
            break;
          case 'firmaPaciente':
            newInput.hidden = !(esPresencial && formData.conformidad === true);
            newInput.required = esPresencial && formData.conformidad === true;
            break;
          // VIRTUAL
          case 'nombreCompleto':
            newInput.hidden = !(esVirtual && formData?.idComboVisitaValorInterlocutor === ROL_PACIENTE);
            newInput.fixedValue = visita?.paciente || "-";
            break;
          case 'interlocutorNombre':
            newInput.hidden = formData?.idComboVisitaValorInterlocutor === ROL_PACIENTE;
            newInput.required = !newInput.hidden;
            break;
          case 'dNI':
            newInput.hidden = !esVirtual;
            break;
          case 'fechaFin':
            newInput.disabled = !!isClone ? true : newInput.disabled;
            break;
          case 'aclaracionFechaFinAuto':
            newInput.hidden = !esVisitaCero;
            break;
          default:
            break;
        }

        if (esConsultor) newInput.hidden = true;

        if (readOnly || esVisitaCero) newInput.disabled = true;
        return newInput;
      });

      return { ...newGroup, inputs: formattedInputs };
    });
  }, [
    readOnly,
    isClone,
    formData?.conformidad,
    formData?.idComboVisitaValorInterlocutor,
    formData?.dNI,
    esConsultor,
    visita?.fechaVisita,
    visita?.paciente,
    visita?.idComboVisitaValorTipoVisita,
    visita?.dNI,
    visita?.dni,
    visita?.idUsuarioCreador,
    comboInterlocutorOptions,
    ROL_PACIENTE,
    ROL_FAMILIAR,
    ROL_CUIDADOR,
    esVisitaCero,
    idUsuario
  ]);
  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const saveVisitaFirma = async (visitaData, finalizar) => {

    const fechaInicio = moment(visita.fechaVisita);
    const fechaFin = moment(visitaData.fechaFin);

    if (!fechaFin.isValid()) {
      toast.error("La fecha de finalización es inválida.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    if (fechaFin.isSameOrBefore(fechaInicio, 'minute') && !esConsultor) {
      toast.error("La fecha de finalización debe ser posterior a la fecha de inicio de la visita.");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    if (fechaFin.isAfter(moment())) {
      toast.error("La fecha de finalización no puede ser mayor a la fecha y hora actual");
      const error = new Error("Validación fallida");
      error.isCustomError = true;
      throw error;
    }

    const coordenadas = await obtenerCoordenadas();

    const { data } = await save({
      variables: { input: adaptAddVisitaFinalizarInput({ ...visitaData, finalizada: finalizar, coordenadas }) }
    });

    const idVisita = data?.saveFinalizarVisita?.idVisita;
    if (!idVisita) throw new Error("Error al guardar la visita");

    return idVisita;
  };


  useStepSave(onSaveRef, async () => {
    const updatedVisita = { ...visita, ...formData };
    await saveVisitaFirma(updatedVisita);
    setVisita(updatedVisita);
  });

  async function tieneGeoHabilitada() {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        resolve(false);
        return;
      }

      const timeoutMs = 60000;

      // Intenta obtener la ubicación con un timeout
      const timeoutId = setTimeout(() => {
        resolve(false);
      }, timeoutMs);

      navigator.geolocation.getCurrentPosition(
        () => {
          clearTimeout(timeoutId);
          resolve(true);
        },
        (error) => {
          clearTimeout(timeoutId);
          // Si el error es PERMISSION_DENIED, no tiene permisos
          // Si es POSITION_UNAVAILABLE o TIMEOUT, puede tener permisos pero hay otros problemas
          if (error.code === error.PERMISSION_DENIED) {
            resolve(false);
          } else {
            // Asumimos que tiene permisos pero hay problemas técnicos
            resolve(true);
          }
        },
        {
          timeout: timeoutMs,
          maximumAge: 0,
          enableHighAccuracy: false // Más rápido para la verificación
        }
      );
    });
  }

  const handleGuardar = async () => {
    try {
      setFinalizarLoading(true);
      const tieneGeo = await tieneGeoHabilitada();

      if (!tieneGeo) {
        toast.error("Debe habilitar el permiso de ubicación en su navegador para crear una nueva visita.", {
          duration: 4000
        });
        return;
      }

      const updatedVisita = { ...visita, ...formData };
      
      if (esVisitaCero || esConsultor) {
        updatedVisita.fechaFin = moment().format('YYYY-MM-DD HH:mm:ss');
      }

      validateStepFields({ formattedGroups, formData: updatedVisita });

      const resultadoValidacion = await validarDistancia(updatedVisita.idVisita, { omitirValidacion: updatedVisita?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_TELEENFERMERIA });
      if (!resultadoValidacion.permitir) return;

      if (!esConsultor) {
        const resValidacionDiscrepancia = await validarDiscrepancia(updatedVisita.idVisita, updatedVisita.fechaVisita, updatedVisita.fechaFin);
        if (!resValidacionDiscrepancia.permitir) return;
      }

      await saveVisitaFirma(updatedVisita, true);
      setVisita(updatedVisita);

      toast.success("Visita guardada correctamente.");
      if (esVisitaCero) {
        navigate(-1);
      } else {
        navigate("/misVisitas");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFinalizarLoading(false);
    }
  };

  useEffect(() => {
    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const interlocutorId = combosVisita.find((c) => c.comboName === COMBO_VISITA_INERLOCUTOR)?.id;

    if (Number.isInteger(interlocutorId)) {
      loadComboInterlocutor({ variables: { idComboVisita: interlocutorId } });
    }
  }, [combosVisita, loadComboInterlocutor]);

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);
      setFormData(prev => ({
        ...filterVisitaData(visita, inputNames),
        idVisita: visita.idVisita,
        conformidad: visita?.conformidad === false ? false : true,
        fechaFin: prev.fechaFin || visita.fechaFin,
        obsConformidad: prev.obsConformidad || visita.obsConformidad,
        idComboVisitaValorInterlocutor: prev.idComboVisitaValorInterlocutor || visita.idComboVisitaValorInterlocutor,
        firmaNombrePacienteInfo: prev.firmaNombrePacienteInfo || visita.firmaNombrePacienteInfo,
        interlocutorNombre: prev.interlocutorNombre || visita.interlocutorNombre,
      }));
    }
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "finalizacionVisita",
      stepName: "Finalización de la visita",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    {input.name !== 'firmaPaciente' && <RenderInputByTypeMobile
                      input={input}
                      value={input.fixedValue || formData[input.name]}
                      handleChange={handleChange}
                      row={input.type === "multiple_boolean"} />
                    }
                    {input.name === 'firmaPaciente' && <VisitaFirmaInput
                      key={'firmaPaciente' + input.name}
                      idVisita={visita.idVisita}
                      handleChange={handleChange}
                      inputName={input.name}
                      readOnly={readOnly} />
                    }
                  </Grid>
                )}
              <Grid size={{ xs: 12 }} sx={esVisitaCero ? { my: 2 } : {}}>
                <CustomButton
                  variant="contained"
                  color="primary"
                  onClick={handleGuardar}
                  disabled={readOnly}
                  fullWidth
                  loading={finalizarLoading}>

                  Finalizar
                </CustomButton>
              </Grid>
            </Grid>
            {modalDistancia}
            {modalDiscrepancia}
          </CardBox>
        )}
    </>);

}

const groups = [
  // PRESENCIAL
  {
    key: 'presencial',
    title: "Finalización",
    inputs: [
      {
        name: "fechaFin",
        displayName: "Egreso",
        disableFuture: true,
        type: "datetime",
        required: true
      },
      {
        name: "conformidad",
        displayName: "Conformidad",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "obsConformidad",
        displayName: "Obs. Conformidad",
        type: "long_string",
        maxLength: 1000
      },
      {
        name: "idComboVisitaValorInterlocutor",
        displayName: "Responsable",
        type: "select",
        required: true
      },
      // Si selecciona Paciente, mostrar nombre y apellido del paciente (solo lectura / info)
      {
        name: "firmaNombrePacienteInfo",
        displayName: "Nombre y apellido",
        type: "infoRow"
      },
      // Si Familiar/Cuidador, pedir nombre y apellido
      {
        name: "interlocutorNombre",
        displayName: "Nombre y apellido",
        type: "string",
        maxLength: 200
      },
      // Firma con el dedo (componente del RI)
      {
        name: "firmaPaciente",
        displayName: "Firma Paciente / Familiar",
        type: "signature" // componente custom en RenderInputByTypeMobile
      }]

  },

  // VIRTUAL
  {
    key: 'virtual',
    title: "Finalización",
    inputs: [
      {
        name: "codigoAtencion",
        displayName: "Código de Atención",
        type: "string",
        maxLength: 100,
        required: true
      },
      {
        name: "fechaFin",
        displayName: "Fecha y hora de finalización",
        type: "datetime",
        disableFuture: true,
        required: true
      },
      {
        name: 'aclaracionFechaFinAuto',
        displayName: 'La fecha de finalización se aplicará automaticamente al Finalizar la visita.',
        type: 'text'
      },
      {
        name: "idComboVisitaValorInterlocutor",
        displayName: "Interlocutor",
        type: "select",
        required: true
      },
      // Si Paciente, mostrar nombre paciente
      {
        name: "nombreCompleto",
        displayName: "Nombre y apellido",
        type: "infoRow"
      },
      // Si Familiar/Cuidador, pedir nombre
      {
        name: "interlocutorNombre",
        displayName: "Nombre y apellido",
        type: "string",
        maxLength: 200
      },
      {
        name: "dNI",
        displayName: "DNI",
        type: "int",
        required: true,
        min: 0,
        max: 99999999
      }]

  }];